import { useMemo } from 'react';
import { env } from '@config/env';
import { useModuleStore } from '@shared/stores/moduleStore';

/**
 * Resolves the imagery shown on the welcome + auth screens.
 *
 * Auth background images are **branding-driven**: they come from the
 * tenant's domain config (`moduleConfig.portal.auth_*_background_image_url`
 * and `moduleConfig.onboarding.welcome_background_image_url`). `moduleStore`
 * holds the full domain config and persists it, so this resolves instantly
 * on cold start from cache.
 *
 * Resolution order ("domain-resolved with a local fallback"):
 *   1. The tenant's configured auth/welcome background image.
 *   2. The per-vertical override in `env.defaultImagery`.
 *   3. The bundled local asset under `public/assets/imagery/`.
 *
 * The welcome hero has no bundled fallback — `heroImage` is `undefined`
 * until branding resolves, and screens render a solid brand backdrop in
 * the meantime (the welcome screen gates behind a loader entirely).
 */

const BUNDLED_AUTH_BG = '/assets/imagery/auth-background.svg';

export interface BrandImagery {
  /**
   * Welcome / tour hero background. `undefined` when no branding-driven
   * image has resolved — consumers fall back to a solid brand backdrop.
   */
  heroImage: string | undefined;
  /** Sign-in screen background. */
  authBackground: string;
  /** Create-account screen background. */
  signupBackground: string;
  /** Forgot-password screen background. */
  forgotBackground: string;
  /** True when the image came from the tenant branding (vs. a local fallback). */
  heroFromBranding: boolean;
  authFromBranding: boolean;
}

export function useBrandImagery(): BrandImagery {
  const getDomainImagery = useModuleStore((s) => s.getDomainImagery);
  const config = useModuleStore((s) => s.config);

  return useMemo(() => {
    const img = getDomainImagery();
    const login = img.loginBackground;
    // Welcome uses the login background, falling back to its own welcome
    // image only when no login background is configured.
    const hero = login ?? img.welcomeHero;
    const fallbackBg = env.defaultImagery?.authBackground ?? BUNDLED_AUTH_BG;
    return {
      heroImage: hero ?? env.defaultImagery?.welcomeHero,
      authBackground: login ?? fallbackBg,
      signupBackground: img.signupBackground ?? login ?? fallbackBg,
      forgotBackground: img.forgotBackground ?? login ?? fallbackBg,
      heroFromBranding: !!hero,
      authFromBranding: !!login,
    };
    // `config` drives recomputation when domain config loads/changes.
  }, [getDomainImagery, config]);
}
